import React, { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '@/store/auth';
import { featureAccessApi } from '@/services/featureAccessApi';

/**
 * Platform Features Enum (mirrored from backend)
 * This should match the PlatformFeature enum in shared-types
 */
export enum PlatformFeature {
  // Core Dashboard Features
  DASHBOARD = 'DASHBOARD',
  DASHBOARD_ANALYTICS = 'DASHBOARD_ANALYTICS',
  DASHBOARD_STATISTICS = 'DASHBOARD_STATISTICS',
  
  // Connector Library & Management
  CONNECTOR_LIBRARY = 'CONNECTOR_LIBRARY',
  CONNECTOR_LIBRARY_VIEW = 'CONNECTOR_LIBRARY_VIEW',
  CONNECTOR_LIBRARY_CREATE = 'CONNECTOR_LIBRARY_CREATE',
  CONNECTOR_LIBRARY_EDIT = 'CONNECTOR_LIBRARY_EDIT',
  CONNECTOR_LIBRARY_DELETE = 'CONNECTOR_LIBRARY_DELETE',
  
  // Integration Features
  INTEGRATION_SETUP = 'INTEGRATION_SETUP',
  INTEGRATION_ENGINE = 'INTEGRATION_ENGINE',
  INTEGRATION_STUDIO = 'INTEGRATION_STUDIO',
  INTEGRATION_PROFILE = 'INTEGRATION_PROFILE',
  
  // Configuration Features
  SOURCE_CONFIGURATION = 'SOURCE_CONFIGURATION',
  DESTINATION_CONFIGURATION = 'DESTINATION_CONFIGURATION',
  ENRICHMENT_CONFIGURATION = 'ENRICHMENT_CONFIGURATION',
  DATA_MAPPING = 'DATA_MAPPING',
  
  // Intelligence & AI Features
  INTELLIGENCE_STUDIO = 'INTELLIGENCE_STUDIO',
  AI_INSIGHTS = 'AI_INSIGHTS',
  AI_CHAT = 'AI_CHAT',

  // Master Data
  DATA_DIMENSION_STUDIO = 'DATA_DIMENSION_STUDIO',
  WORKSPACE_LENSES = 'WORKSPACE_LENSES',

  // Fleet telematics
  FLEET_TRACKING = 'FLEET_TRACKING',
  
  // Automation Features
  AUTOMATION_STUDIO = 'AUTOMATION_STUDIO',
  WORKFLOW_BUILDER = 'WORKFLOW_BUILDER',
  
  // Account & User Management
  ACCOUNT = 'ACCOUNT',
  ACCOUNT_PROFILE = 'ACCOUNT_PROFILE',
  ACCOUNT_SECURITY = 'ACCOUNT_SECURITY',
  ACCOUNT_BILLING = 'ACCOUNT_BILLING',
  
  // Support Features
  SUPPORT_FAQ = 'SUPPORT_FAQ',
  HELP_CENTER = 'HELP_CENTER',
  SUPPORT_TICKETS = 'SUPPORT_TICKETS',
  
  // System Settings
  SYSTEM_SETTINGS = 'SYSTEM_SETTINGS',
  SYSTEM_SETTINGS_GENERAL = 'SYSTEM_SETTINGS_GENERAL',
  SYSTEM_SETTINGS_SECURITY = 'SYSTEM_SETTINGS_SECURITY',
  SYSTEM_SETTINGS_NOTIFICATIONS = 'SYSTEM_SETTINGS_NOTIFICATIONS',
  SYSTEM_SETTINGS_INTEGRATIONS = 'SYSTEM_SETTINGS_INTEGRATIONS',
  SYSTEM_SETTINGS_API_KEYS = 'SYSTEM_SETTINGS_API_KEYS',
  
  // Custom Processors
  CUSTOM_PROCESSORS = 'CUSTOM_PROCESSORS',
  PROCESSOR_EDITOR = 'PROCESSOR_EDITOR',
  
  // Admin Console Features
  ADMIN_CONSOLE = 'ADMIN_CONSOLE',
  ADMIN_CUSTOMERS = 'ADMIN_CUSTOMERS',
  ADMIN_USERS = 'ADMIN_USERS',
  ADMIN_CONNECTORS = 'ADMIN_CONNECTORS',
  ADMIN_RUNTIME = 'ADMIN_RUNTIME',
  ADMIN_KUBECONFIG = 'ADMIN_KUBECONFIG',
  ADMIN_AI_AGENTS = 'ADMIN_AI_AGENTS',
  ADMIN_BILLING = 'ADMIN_BILLING',
  ADMIN_PLANS = 'ADMIN_PLANS',
  ADMIN_AZURE_COSTS = 'ADMIN_AZURE_COSTS',
  ADMIN_PROCESSORS = 'ADMIN_PROCESSORS',
  ADMIN_AUDIT_LOGS = 'ADMIN_AUDIT_LOGS',
  ADMIN_FEATURE_MANAGEMENT = 'ADMIN_FEATURE_MANAGEMENT',
  
  // Developer Features
  API_DOCUMENTATION = 'API_DOCUMENTATION',
  WEBHOOK_MANAGEMENT = 'WEBHOOK_MANAGEMENT',
  
  // Advanced Features
  DATA_EXPORT = 'DATA_EXPORT',
  DATA_IMPORT = 'DATA_IMPORT',
  BULK_OPERATIONS = 'BULK_OPERATIONS',
  SCHEDULED_JOBS = 'SCHEDULED_JOBS',
}

export enum FeatureAccessLevel {
  NONE = 'NONE',
  READ = 'READ',
  WRITE = 'WRITE',
  FULL = 'FULL',
}

export interface FeatureAccessInfo {
  feature: PlatformFeature;
  name: string;
  description: string;
  category: string;
  hasAccess: boolean;
  accessLevel: FeatureAccessLevel;
  source: 'ROLE_DEFAULT' | 'COMPANY' | 'USER_OVERRIDE' | 'SUPER_ADMIN';
  reason?: string;
  expiresAt?: Date;
}

/**
 * Hook to check if user has access to a specific feature
 * 
 * Usage:
 * const { hasAccess, loading, accessInfo } = useFeatureAccess(PlatformFeature.ADMIN_CONSOLE);
 * 
 * if (loading) return <Spinner />;
 * if (!hasAccess) return <AccessDenied />;
 * return <AdminConsole />;
 */
export function useFeatureAccess(feature: PlatformFeature) {
  const [hasAccess, setHasAccess] = useState<boolean>(false);
  const [accessInfo, setAccessInfo] = useState<FeatureAccessInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const user = useAuthStore((state) => state.user);
  const loggedIn = useAuthStore((state) => state.loggedIn);

  const checkAccess = useCallback(async () => {
    if (!loggedIn || !user) {
      setHasAccess(false);
      setLoading(false);
      return;
    }

    // SUPER_ADMIN always has access
    if (user.role === 'SUPER_ADMIN') {
      setHasAccess(true);
      setAccessInfo({
        feature,
        name: feature,
        description: '',
        category: '',
        hasAccess: true,
        accessLevel: FeatureAccessLevel.FULL,
        source: 'SUPER_ADMIN',
      });
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await featureAccessApi.checkFeatureAccess(feature);
      setHasAccess(response.hasAccess);
      setAccessInfo(response);
      setError(null);
    } catch (err) {
      console.error(`Error checking access for feature ${feature}:`, err);
      setError(err as Error);
      setHasAccess(false);
      setAccessInfo(null);
    } finally {
      setLoading(false);
    }
  }, [feature, loggedIn, user]);

  useEffect(() => {
    checkAccess();
  }, [checkAccess]);

  return {
    hasAccess,
    accessInfo,
    loading,
    error,
    refetch: checkAccess,
  };
}

/**
 * Hook to check multiple features at once
 * 
 * Usage:
 * const { featuresAccess, loading } = useMultipleFeatureAccess([
 *   PlatformFeature.ADMIN_CONSOLE,
 *   PlatformFeature.ADMIN_USERS,
 * ]);
 */
export function useMultipleFeatureAccess(features: PlatformFeature[]) {
  const [featuresAccess, setFeaturesAccess] = useState<Record<PlatformFeature, boolean>>({} as any);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const user = useAuthStore((state) => state.user);
  const loggedIn = useAuthStore((state) => state.loggedIn);

  const checkAccess = useCallback(async () => {
    if (!loggedIn || !user) {
      const access: Record<PlatformFeature, boolean> = {} as any;
      features.forEach(f => access[f] = false);
      setFeaturesAccess(access);
      setLoading(false);
      return;
    }

    // SUPER_ADMIN always has access
    if (user.role === 'SUPER_ADMIN') {
      const access: Record<PlatformFeature, boolean> = {} as any;
      features.forEach(f => access[f] = true);
      setFeaturesAccess(access);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const promises = features.map(f => featureAccessApi.checkFeatureAccess(f));
      const results = await Promise.all(promises);
      
      const access: Record<PlatformFeature, boolean> = {} as any;
      results.forEach((result, index) => {
        access[features[index]] = result.hasAccess;
      });
      
      setFeaturesAccess(access);
      setError(null);
    } catch (err) {
      console.error('Error checking multiple feature access:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [features, loggedIn, user]);

  useEffect(() => {
    checkAccess();
  }, [checkAccess]);

  return {
    featuresAccess,
    loading,
    error,
    refetch: checkAccess,
  };
}

/**
 * Hook to get all feature access for the current user
 * 
 * Usage:
 * const { allFeatures, loading } = useAllFeatureAccess();
 */
export function useAllFeatureAccess() {
  const [allFeatures, setAllFeatures] = useState<FeatureAccessInfo[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const user = useAuthStore((state) => state.user);
  const loggedIn = useAuthStore((state) => state.loggedIn);

  const fetchAllAccess = useCallback(async () => {
    if (!loggedIn || !user) {
      setAllFeatures([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await featureAccessApi.getMyFeatureAccess();
      setAllFeatures(response.features);
      setError(null);
    } catch (err) {
      console.error('Error fetching all feature access:', err);
      setError(err as Error);
      setAllFeatures([]);
    } finally {
      setLoading(false);
    }
  }, [loggedIn, user]);

  useEffect(() => {
    fetchAllAccess();
  }, [fetchAllAccess]);

  return {
    allFeatures,
    loading,
    error,
    refetch: fetchAllAccess,
  };
}

/**
 * Hook to check route access
 * 
 * Usage:
 * const { hasAccess, loading } = useRouteAccess('/admin');
 */
export function useRouteAccess(route: string) {
  const [hasAccess, setHasAccess] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const user = useAuthStore((state) => state.user);
  const loggedIn = useAuthStore((state) => state.loggedIn);

  const checkAccess = useCallback(async () => {
    if (!loggedIn || !user) {
      setHasAccess(false);
      setLoading(false);
      return;
    }

    // SUPER_ADMIN always has access
    if (user.role === 'SUPER_ADMIN') {
      setHasAccess(true);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await featureAccessApi.checkRouteAccess(route);
      setHasAccess(response.hasAccess);
      setError(null);
    } catch (err) {
      console.error(`Error checking route access for ${route}:`, err);
      setError(err as Error);
      setHasAccess(false);
    } finally {
      setLoading(false);
    }
  }, [route, loggedIn, user]);

  useEffect(() => {
    checkAccess();
  }, [checkAccess]);

  return {
    hasAccess,
    loading,
    error,
    refetch: checkAccess,
  };
}

/**
 * Higher-order component to protect routes with feature access
 * 
 * Usage:
 * export default withFeatureAccess(MyComponent, PlatformFeature.ADMIN_CONSOLE);
 */
export function withFeatureAccess<P extends object>(
  Component: React.ComponentType<P>,
  feature: PlatformFeature,
  FallbackComponent?: React.ComponentType
) {
  return function FeatureProtectedComponent(props: P) {
    const { hasAccess, loading } = useFeatureAccess(feature);

    if (loading) {
      return (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh' 
        }}>
          Loading...
        </div>
      );
    }

    if (!hasAccess) {
      if (FallbackComponent) {
        return <FallbackComponent />;
      }
      
      return (
        <div style={{ 
          padding: '24px', 
          textAlign: 'center' 
        }}>
          <h2>Access Denied</h2>
          <p>You do not have permission to access this feature.</p>
        </div>
      );
    }

    return <Component {...props} />;
  };
}

